import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  MinusCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  StopOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import {
  EXPERIMENT_KEY_PATTERN,
  MAX_RUNNING_EXPERIMENTS,
  defaultVariants,
  editableFields,
  formatVariants,
  variantProblems,
  weightSum,
  type ExperimentField,
  type ExperimentStatus,
  type ExperimentVariant,
} from './variants';

const { Title, Text } = Typography;

interface ExperimentRow {
  id: string;
  key: string;
  name: string;
  status: ExperimentStatus;
  variants: ExperimentVariant[];
  startsAt: string | null;
  endsAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ExperimentDetail extends ExperimentRow {
  assignmentCounts?: Record<string, number>;
}

interface ExperimentFormValues {
  key?: string;
  name?: string;
  variants?: ExperimentVariant[];
  startsAt?: Dayjs | null;
  endsAt?: Dayjs | null;
  notes?: string | null;
}

const STATUS_COLOR: Record<ExperimentStatus, string> = {
  draft: 'default',
  running: 'green',
  stopped: 'red',
};

const START_CONFIRM = 'Players are assigned from now on. Variants can no longer change.';
const STOP_CONFIRM =
  'Stopping ends the experiment for everyone: variant offers and catalogue rules stop matching. It cannot be restarted.';

const formatTime = (value: string | null | undefined): string =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';

const toIso = (value: Dayjs | null | undefined): string | null =>
  value && value.isValid() ? value.toISOString() : null;

export const ExperimentsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ExperimentRow[]>([]);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExperimentRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [form] = Form.useForm<ExperimentFormValues>();
  const watchedVariants = Form.useWatch('variants', form);

  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [detail, setDetail] = useState<ExperimentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getExperiments();
      const data = res.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch experiments', error);
      message.error(apiErrorMessage(error, 'Failed to load experiments'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!drawerKey) {
      setDetail(null);
      setDetailError(null);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailLoading(true);
    setDetailError(null);
    adminApi
      .getExperiment(drawerKey)
      .then((res) => {
        if (!cancelled) setDetail(res.data as ExperimentDetail);
      })
      .catch((error) => {
        console.error('Failed to fetch experiment', error);
        if (!cancelled) setDetailError(apiErrorMessage(error, 'Failed to load the experiment'));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [drawerKey]);

  const runningCount = rows.filter((r) => r.status === 'running').length;

  const openCreate = () => {
    setEditing(null);
    setProblems([]);
    form.resetFields();
    form.setFieldsValue({ variants: defaultVariants() });
    setModalOpen(true);
  };

  const openEdit = (row: ExperimentRow) => {
    setEditing(row);
    setProblems([]);
    form.resetFields();
    form.setFieldsValue({
      key: row.key,
      name: row.name,
      variants: row.variants.map((v) => ({ ...v })),
      startsAt: row.startsAt ? dayjs(row.startsAt) : null,
      endsAt: row.endsAt ? dayjs(row.endsAt) : null,
      notes: row.notes,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setProblems([]);
  };

  const editable: ExperimentField[] = editing ? editableFields(editing.status) : [];
  const canEdit = (field: ExperimentField): boolean => !editing || editable.includes(field);

  const handleSubmit = async (values: ExperimentFormValues) => {
    const variantsEditable = canEdit('variants');
    if (variantsEditable) {
      const found = variantProblems(values.variants);
      if (found.length > 0) {
        setProblems(found);
        return;
      }
    }
    setProblems([]);
    setSaving(true);
    try {
      if (editing) {
        const payload: Record<string, unknown> = {};
        if (canEdit('name')) payload.name = values.name;
        if (canEdit('notes')) payload.notes = values.notes?.trim() ? values.notes : null;
        if (canEdit('startsAt')) payload.startsAt = toIso(values.startsAt);
        if (canEdit('endsAt')) payload.endsAt = toIso(values.endsAt);
        if (variantsEditable) payload.variants = values.variants;
        await adminApi.updateExperiment(editing.key, payload);
        message.success(`Experiment ${editing.key} updated`);
      } else {
        await adminApi.createExperiment({
          key: values.key,
          name: values.name,
          variants: values.variants,
          startsAt: toIso(values.startsAt),
          endsAt: toIso(values.endsAt),
          notes: values.notes?.trim() ? values.notes : null,
        });
        message.success(`Experiment ${values.key} created`);
      }
      closeModal();
      fetchData();
    } catch (error) {
      console.error('Failed to save experiment', error);
      setProblems([apiErrorMessage(error, 'Failed to save the experiment')]);
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (
    row: ExperimentRow,
    action: () => Promise<unknown>,
    done: string,
    failed: string
  ) => {
    setBusyKey(row.key);
    try {
      await action();
      message.success(done);
      await fetchData();
    } catch (error) {
      console.error(failed, error);
      message.error(apiErrorMessage(error, failed));
    } finally {
      setBusyKey(null);
    }
  };

  const columns: ColumnsType<ExperimentRow> = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (value: string) => <Text code>{value}</Text>,
    },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ExperimentStatus) => <Tag color={STATUS_COLOR[status]}>{status}</Tag>,
    },
    {
      title: 'Variants',
      dataIndex: 'variants',
      key: 'variants',
      render: (variants: ExperimentVariant[]) => formatVariants(variants),
    },
    {
      title: 'Starts',
      dataIndex: 'startsAt',
      key: 'startsAt',
      render: (value: string | null) => formatTime(value),
    },
    {
      title: 'Ends',
      dataIndex: 'endsAt',
      key: 'endsAt',
      render: (value: string | null) => formatTime(value),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, row) => (
        <Space wrap>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDrawerKey(row.key)}>
            Details
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={editableFields(row.status).length === 0}
            onClick={() => openEdit(row)}
          >
            Edit
          </Button>
          {row.status === 'draft' && (
            <Popconfirm
              title="Start experiment?"
              description={START_CONFIRM}
              okText="Start"
              onConfirm={() =>
                runAction(
                  row,
                  () => adminApi.startExperiment(row.key),
                  `Experiment ${row.key} started`,
                  'Failed to start the experiment'
                )
              }
            >
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                loading={busyKey === row.key}
              >
                Start
              </Button>
            </Popconfirm>
          )}
          {row.status === 'running' && (
            <Popconfirm
              title="Stop experiment?"
              description={STOP_CONFIRM}
              okText="Stop"
              okButtonProps={{ danger: true }}
              onConfirm={() =>
                runAction(
                  row,
                  () => adminApi.stopExperiment(row.key),
                  `Experiment ${row.key} stopped`,
                  'Failed to stop the experiment'
                )
              }
            >
              <Button size="small" danger icon={<StopOutlined />} loading={busyKey === row.key}>
                Stop
              </Button>
            </Popconfirm>
          )}
          {row.status === 'draft' && (
            <Popconfirm
              title="Delete this draft?"
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() =>
                runAction(
                  row,
                  () => adminApi.deleteExperiment(row.key),
                  `Experiment ${row.key} deleted`,
                  'Failed to delete the experiment'
                )
              }
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={busyKey === row.key}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const sum = weightSum(watchedVariants);

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <ExperimentOutlined /> Experiments
            </Title>
            <Text type={runningCount >= MAX_RUNNING_EXPERIMENTS ? 'danger' : 'secondary'}>
              {runningCount} of {MAX_RUNNING_EXPERIMENTS} running
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New experiment
            </Button>
          </Space>
        </div>
        <Table<ExperimentRow>
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editing ? `Edit experiment ${editing.key}` : 'New experiment'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={saving}
        width={640}
        destroyOnClose
        forceRender
      >
        <Form<ExperimentFormValues> form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="key"
            label="Key"
            extra="lowercase, up to 20 characters; the app reports it to GA4 as exp_<key>"
            rules={[
              { required: true, message: 'Key is required' },
              {
                pattern: EXPERIMENT_KEY_PATTERN,
                message: 'Start with a letter, then a-z, 0-9 or _ (20 characters at most)',
              },
            ]}
          >
            <Input disabled={!!editing} placeholder="starter_price" maxLength={20} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: 'Name is required' },
              { max: 100, message: 'At most 100 characters' },
            ]}
          >
            <Input disabled={!canEdit('name')} maxLength={100} />
          </Form.Item>

          <Form.Item
            label={
              <Space>
                <span>Variants</span>
                <Text type={sum === 100 ? 'success' : 'danger'}>sum {sum} / 100</Text>
              </Space>
            }
            required
          >
            <Form.List name="variants">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ display: 'flex' }}>
                      <Form.Item
                        name={[field.name, 'key']}
                        rules={[{ required: true, message: 'Variant key' }]}
                      >
                        <Input
                          placeholder="variant key"
                          maxLength={20}
                          disabled={!canEdit('variants')}
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'weight']}
                        rules={[{ required: true, message: 'Weight' }]}
                      >
                        <InputNumber
                          min={1}
                          max={99}
                          precision={0}
                          addonAfter="%"
                          disabled={!canEdit('variants')}
                        />
                      </Form.Item>
                      {canEdit('variants') && fields.length > 2 && (
                        <MinusCircleOutlined onClick={() => remove(field.name)} />
                      )}
                    </Space>
                  ))}
                  {canEdit('variants') && fields.length < 5 && (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={() => add({ key: '', weight: 1 })}
                    >
                      Add variant
                    </Button>
                  )}
                </>
              )}
            </Form.List>
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item name="startsAt" label="Starts">
              <DatePicker showTime disabled={!canEdit('startsAt')} />
            </Form.Item>
            <Form.Item name="endsAt" label="Ends">
              <DatePicker showTime disabled={!canEdit('endsAt')} />
            </Form.Item>
          </Space>

          <Form.Item
            name="notes"
            label="Notes"
            rules={[{ max: 2000, message: 'At most 2000 characters' }]}
          >
            <Input.TextArea rows={3} disabled={!canEdit('notes')} maxLength={2000} />
          </Form.Item>

          {problems.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Cannot save"
              description={
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {problems.map((p, i) => (
                    <li key={`${i}-${p}`}>{p}</li>
                  ))}
                </ul>
              }
            />
          )}
        </Form>
      </Modal>

      <Drawer
        title={drawerKey ? `Experiment ${drawerKey}` : 'Experiment'}
        open={!!drawerKey}
        onClose={() => setDrawerKey(null)}
        width={480}
        loading={detailLoading}
      >
        {detailError && <Alert type="error" showIcon message={detailError} />}
        {detail && (
          <>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Name">{detail.name}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Starts">{formatTime(detail.startsAt)}</Descriptions.Item>
              <Descriptions.Item label="Ends">{formatTime(detail.endsAt)}</Descriptions.Item>
              <Descriptions.Item label="Notes">{detail.notes || '-'}</Descriptions.Item>
            </Descriptions>
            <Title level={5} style={{ marginTop: 24 }}>
              Assigned players per variant
            </Title>
            <Table<ExperimentVariant>
              rowKey="key"
              size="small"
              pagination={false}
              dataSource={detail.variants}
              columns={[
                { title: 'Variant', dataIndex: 'key', key: 'key' },
                {
                  title: 'Weight',
                  dataIndex: 'weight',
                  key: 'weight',
                  render: (w: number) => `${w}%`,
                },
                {
                  title: 'Players',
                  key: 'players',
                  render: (_: unknown, v) => detail.assignmentCounts?.[v.key] ?? 0,
                },
              ]}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
              Results: WS4 <Text code>v_experiment_results</Text>, compared against control
            </Text>
          </>
        )}
      </Drawer>
    </div>
  );
};
